// Estado do app. Só online (P0.9): a tela muda na hora e, se o servidor recusar ou a
// rede falhar, volta ao estado anterior com um aviso (ASM-071 / AC-144). Offline ou sem
// assinatura, as ações ficam desativadas (AC-143 / AC-107).
import { createContext, useContext, useEffect, useRef, useSyncExternalStore, type ReactNode } from 'react';
import { hasAccess, type Subscription } from '../domain/access';
import { toDateKey } from '../domain/dates';
import { rankPositions } from '../domain/manualOrder';
import { pendingForToday } from '../domain/ordering';
import type { RecurringFields, RecurringTask } from '../domain/recurring';
import type { Profile, ProfilePatch } from '../domain/settings';
import type { Task, TaskFields } from '../domain/task';
import { applyFinish, applyPause, applyStart, elapsedSeconds, finishResult, type FinishResult } from '../domain/timer';
import type { Session } from '../data/auth';
import { BackendError, type Backend } from '../data/repository';
import { startConnectivityMonitor } from './connectivity';

export interface Toast {
  id: number;
  kind: 'error' | 'info';
  key: string;
}

export interface Celebration {
  taskId: string;
  title: string;
  result: FinishResult;
  /** era a última tarefa pendente de hoje (AC-030) */
  endOfDay: boolean;
}

export interface AppState {
  phase: 'loading' | 'signedOut' | 'ready';
  session: Session | null;
  tasks: Task[];
  /** moldes das tarefas recorrentes (US-095) */
  recurring: RecurringTask[];
  profile: Profile | null;
  subscription: Subscription | null;
  online: boolean;
  /** hora do servidor − hora do aparelho (ms) */
  clockOffset: number;
  toast: Toast | null;
  celebration: Celebration | null;
}

export type ActionSource = 'user' | 'pomodoro' | 'float';
export type StoreEvent =
  | { type: 'started'; id: string; source: ActionSource }
  | { type: 'paused'; id: string; source: ActionSource }
  | { type: 'finishing'; id: string; source: ActionSource }
  | { type: 'finishFailed'; id: string; source: ActionSource }
  | { type: 'finished'; id: string; source: ActionSource };

export interface AppStoreOptions {
  now?: () => number;
}

let toastSeq = 0;
let tempSeq = 0;

export function createAppStore(backend: Backend, opts: AppStoreOptions = {}) {
  const clock = opts.now ?? (() => Date.now());
  let state: AppState = {
    phase: 'loading',
    session: null,
    tasks: [],
    recurring: [],
    profile: null,
    subscription: null,
    online: true,
    clockOffset: 0,
    toast: null,
    celebration: null,
  };
  const listeners = new Set<() => void>();
  const eventListeners = new Set<(e: StoreEvent) => void>();
  let unsubscribeAuth: (() => void) | null = null;

  const get = () => state;
  const set = (patch: Partial<AppState>) => {
    state = { ...state, ...patch };
    listeners.forEach((l) => l());
  };
  const emit = (e: StoreEvent) => eventListeners.forEach((l) => l(e));
  const now = () => clock() + state.clockOffset;
  const nowIso = () => new Date(now()).toISOString();

  const toast = (key: string, kind: Toast['kind'] = 'error') => set({ toast: { id: ++toastSeq, kind, key } });

  const errorKey = (err: unknown): string => {
    if (err instanceof BackendError) {
      if (err.code === 'network') return 'errors.network';
      if (err.code === 'no_access') return 'errors.noAccess';
      if (err.code === 'invalid') return 'errors.invalid';
    }
    return 'errors.actionFailed';
  };

  const canEdit = () => state.online && hasAccess(state.subscription, now());

  const blockedReason = (): string | null => {
    if (!state.online) return 'errors.offline';
    if (!hasAccess(state.subscription, now())) return 'errors.noAccess';
    return null;
  };

  const mergeTasks = (base: Task[], changed: Task[]): Task[] => {
    const byId = new Map(changed.map((t) => [t.id, t]));
    const merged = base.map((t) => byId.get(t.id) ?? t);
    for (const t of changed) if (!base.some((b) => b.id === t.id)) merged.push(t);
    return merged;
  };

  /** Aplica na tela, envia ao servidor, volta ao estado anterior se falhar. */
  async function mutate(optimistic: (tasks: Task[]) => Task[], send: () => Promise<Task[] | void>): Promise<boolean> {
    const blocked = blockedReason();
    if (blocked) {
      toast(blocked);
      return false;
    }
    const before = state.tasks;
    set({ tasks: optimistic(before) });
    try {
      const changed = await send();
      if (changed) set({ tasks: mergeTasks(state.tasks, changed) });
      return true;
    } catch (err) {
      set({ tasks: before });
      toast(errorKey(err));
      if (err instanceof BackendError && err.code === 'network') void checkOnline();
      if (err instanceof BackendError && err.code === 'no_access') void refreshSubscription();
      return false;
    }
  }

  async function checkOnline() {
    const ok = await backend.ping().catch(() => false);
    setOnline(ok);
  }

  async function syncClock() {
    try {
      const before = clock();
      const server = await backend.serverNow();
      const after = clock();
      set({ clockOffset: Math.round(server - (before + after) / 2) });
    } catch {
      /* mantém a última diferença conhecida */
    }
  }

  async function load() {
    try {
      const [tasks, profile, subscription, recurring] = await Promise.all([
        backend.tasks.list(),
        backend.profile.get(),
        backend.subscription.get(),
        backend.recurring.list(),
      ]);
      set({ tasks, profile, subscription, recurring, phase: 'ready', online: true });
      void syncClock();
      void syncRecurring();
    } catch (err) {
      if (err instanceof BackendError && err.code === 'unauthenticated') {
        set({ phase: 'signedOut', session: null });
        return;
      }
      set({ phase: 'ready', online: !(err instanceof BackendError && err.code === 'network') });
      toast(errorKey(err));
    }
  }

  async function handleSession(session: Session | null) {
    if (!session) {
      set({ phase: 'signedOut', session: null, tasks: [], recurring: [], profile: null, subscription: null, celebration: null });
      return;
    }
    const sameUser = state.session?.user.id === session.user.id;
    set({ session, phase: sameUser && state.phase === 'ready' ? 'ready' : 'loading' });
    if (!sameUser || state.phase !== 'ready') await load();
  }

  async function refreshSubscription() {
    if (!state.session) return;
    try {
      set({ subscription: await backend.subscription.get() });
    } catch {
      /* tenta de novo no próximo foco */
    }
  }

  function setOnline(online: boolean) {
    const was = state.online;
    if (was === online) return;
    set({ online });
    if (online && !was) void reload();
  }

  /** Recarrega do servidor (abrir o app, voltar o foco, reconectar — P0.9). */
  async function reload() {
    if (!state.session) return;
    try {
      const [tasks, subscription] = await Promise.all([backend.tasks.list(), backend.subscription.get()]);
      set({ tasks, subscription });
      await syncRecurring();
    } catch (err) {
      if (err instanceof BackendError && err.code === 'network') void checkOnline();
    }
  }

  const today = () => toDateKey(new Date(now()));

  /**
   * Cria as ocorrências das recorrentes que faltam, de hoje até 7 dias (ASM-108 / ASM-116).
   * Roda ao abrir o app e ao voltar o foco; só com as recorrentes ligadas e com acesso.
   */
  async function syncRecurring() {
    if (!state.profile?.recurring_enabled || !canEdit()) return;
    try {
      const created = await backend.recurring.sync(today());
      if (created.length) set({ tasks: mergeTasks(state.tasks, created) });
    } catch (err) {
      if (err instanceof BackendError && err.code === 'network') void checkOnline();
    }
  }

  /** Mexer numa recorrente muda várias ocorrências de uma vez: busca a lista de novo. */
  async function refreshTasks() {
    set({ tasks: await backend.tasks.list() });
  }

  /** Ações das recorrentes: bloqueadas offline ou sem acesso; erro vira aviso. */
  async function recurringAction(run: () => Promise<void>): Promise<boolean> {
    const blocked = blockedReason();
    if (blocked) {
      toast(blocked);
      return false;
    }
    try {
      await run();
      return true;
    } catch (err) {
      toast(errorKey(err));
      if (err instanceof BackendError && err.code === 'network') void checkOnline();
      if (err instanceof BackendError && err.code === 'no_access') void refreshSubscription();
      return false;
    }
  }

  const store = {
    get,
    subscribe(l: () => void) {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    on(l: (e: StoreEvent) => void) {
      eventListeners.add(l);
      return () => eventListeners.delete(l);
    },
    backend,
    now,
    canEdit,

    async init() {
      unsubscribeAuth?.();
      unsubscribeAuth = backend.auth.onChange((s) => void handleSession(s));
      await handleSession(await backend.auth.getSession());
    },
    reload,
    load,
    syncRecurring,
    setOnline,
    refreshSubscription,
    syncClock,

    async createTask(fields: TaskFields): Promise<boolean> {
      const ts = nowIso();
      const temp: Task = {
        id: `tmp-${++tempSeq}`,
        user_id: state.session?.user.id ?? '',
        ...fields,
        status: 'todo',
        elapsed_seconds: 0,
        running_since: null,
        completed_at: null,
        created_at: ts,
        updated_at: ts,
      };
      return mutate(
        (tasks) => [...tasks, temp],
        async () => {
          const created = await backend.tasks.create(fields);
          set({ tasks: state.tasks.map((t) => (t.id === temp.id ? created : t)) });
        }
      );
    },

    updateTask(id: string, fields: Partial<TaskFields>) {
      return mutate(
        (tasks) =>
          tasks.map((t) => {
            if (t.id !== id) return t;
            const next = { ...t, ...fields, updated_at: nowIso() };
            // prazo editado volta à ordem automática (AC-184)
            const dueChanged = next.due_date !== t.due_date || next.due_time !== t.due_time;
            return dueChanged ? { ...next, position: null } : next;
          }),
        async () => [await backend.tasks.update(id, fields)]
      );
    },

    /**
     * Soltar uma tarefa arrastada (US-007): muda o prazo, se for o caso, e grava a nova
     * ordem da seção de destino. A tela muda na hora e volta se o servidor recusar.
     */
    moveTask(drop: { taskId: string; patch?: { due_date: string | null; due_time: string | null }; ids?: string[] | null }) {
      const positions = drop.ids ? rankPositions(drop.ids) : null;
      return mutate(
        (tasks) =>
          tasks.map((t) => {
            let next = t;
            if (drop.patch && t.id === drop.taskId) next = { ...next, ...drop.patch, position: null, updated_at: nowIso() };
            if (positions?.has(t.id)) next = { ...next, position: positions.get(t.id)! };
            return next;
          }),
        async () => {
          if (drop.patch) await backend.tasks.update(drop.taskId, drop.patch);
          if (drop.ids) await backend.tasks.reorder(drop.ids);
        }
      );
    },

    // ---------- tarefas recorrentes (US-095 / US-097) ----------

    /**
     * Liga ou desliga as recorrentes (AC-216). Desligar tira da fila as ocorrências não
     * iniciadas de hoje em diante (ASM-113); ligar de novo traz elas de volta.
     */
    async setRecurringEnabled(on: boolean): Promise<boolean> {
      const ok = await store.updateProfile({ recurring_enabled: on });
      if (!ok) return false;
      try {
        if (on) await syncRecurring();
        else {
          await backend.recurring.clear(null, today());
          await refreshTasks();
        }
      } catch (err) {
        toast(errorKey(err));
      }
      return true;
    },

    createRecurring(fields: RecurringFields) {
      return recurringAction(async () => {
        const rec = await backend.recurring.create(fields);
        set({ recurring: [...state.recurring, rec] });
        await syncRecurring();
      });
    },

    /** Editar vale para as ocorrências não iniciadas de hoje em diante (AC-218). */
    updateRecurring(id: string, fields: RecurringFields) {
      return recurringAction(async () => {
        const rec = await backend.recurring.update(id, fields);
        set({ recurring: state.recurring.map((r) => (r.id === id ? rec : r)) });
        await backend.recurring.clear(id, today());
        await refreshTasks();
        await syncRecurring();
      });
    },

    /** Excluir tira da fila só as não iniciadas; as outras viram tarefas comuns (AC-218). */
    deleteRecurring(id: string) {
      return recurringAction(async () => {
        await backend.recurring.clear(id, today());
        await backend.recurring.remove(id);
        set({ recurring: state.recurring.filter((r) => r.id !== id) });
        await refreshTasks();
      });
    },

    /** Lixeira de "Tarefas recorrentes": exclui todas, como excluir uma por uma (AC-236). */
    deleteAllRecurring() {
      return recurringAction(async () => {
        for (const rec of state.recurring) {
          await backend.recurring.clear(rec.id, today());
          await backend.recurring.remove(rec.id);
        }
        set({ recurring: [] });
        await refreshTasks();
      });
    },

    deleteTask(id: string) {
      return mutate(
        (tasks) => tasks.filter((t) => t.id !== id),
        async () => {
          await backend.tasks.remove(id);
        }
      );
    },

    /** Lixeira no título de uma seção: apaga de uma vez todas as tarefas dela (AC-235). */
    deleteTasks(ids: string[]) {
      const gone = new Set(ids);
      return mutate(
        (tasks) => tasks.filter((t) => !gone.has(t.id)),
        async () => {
          for (const id of ids) await backend.tasks.remove(id);
        }
      );
    },

    async start(id: string, source: ActionSource = 'user') {
      const ok = await mutate(
        (tasks) => applyStart(tasks, id, nowIso()),
        () => backend.tasks.start(id)
      );
      if (ok) {
        set({ celebration: null });
        emit({ type: 'started', id, source });
      }
      return ok;
    },

    async pause(id: string, source: ActionSource = 'user') {
      const ok = await mutate(
        (tasks) => applyPause(tasks, id, nowIso()),
        async () => [await backend.tasks.pause(id)]
      );
      if (ok) emit({ type: 'paused', id, source });
      return ok;
    },

    async finish(id: string, source: ActionSource = 'user') {
      const before = state.tasks;
      const task = before.find((t) => t.id === id);
      if (!task) return false;
      const wasPendingToday = pendingForToday(before, new Date(now())).some((t) => t.id === id);
      // avisa antes da mudança otimista: a barra flutuante não some antes do "Feito!"
      emit({ type: 'finishing', id, source });
      const ok = await mutate(
        (tasks) => applyFinish(tasks, id, nowIso()),
        async () => [await backend.tasks.finish(id)]
      );
      if (!ok) {
        emit({ type: 'finishFailed', id, source });
        return false;
      }
      const done = state.tasks.find((t) => t.id === id) ?? task;
      const after = new Date(now());
      set({
        celebration: {
          taskId: id,
          title: task.title,
          result: finishResult(elapsedSeconds(done, after.getTime()), task.estimate_minutes),
          endOfDay: wasPendingToday && pendingForToday(state.tasks, after).length === 0,
        },
      });
      emit({ type: 'finished', id, source });
      return true;
    },

    async updateProfile(patch: ProfilePatch): Promise<boolean> {
      if (!state.profile) return false;
      if (!state.online) {
        toast('errors.offline');
        return false;
      }
      const before = state.profile;
      set({ profile: { ...before, ...patch } });
      try {
        set({ profile: await backend.profile.update(patch) });
        return true;
      } catch (err) {
        set({ profile: before });
        toast(errorKey(err));
        return false;
      }
    },

    async signOut() {
      await backend.auth.signOut();
    },

    async deleteAccount(): Promise<boolean> {
      try {
        await backend.deleteAccount();
        return true;
      } catch (err) {
        toast(errorKey(err));
        return false;
      }
    },

    toast,
    dismissToast() {
      set({ toast: null });
    },
    dismissCelebration() {
      set({ celebration: null });
    },
    dispose() {
      unsubscribeAuth?.();
      listeners.clear();
      eventListeners.clear();
    },
  };
  return store;
}

export type AppStore = ReturnType<typeof createAppStore>;

const StoreContext = createContext<AppStore | null>(null);

export function useStore(): AppStore {
  const s = useContext(StoreContext);
  if (!s) throw new Error('AppProvider ausente');
  return s;
}

export function useAppState<T>(selector: (s: AppState) => T): T {
  const store = useStore();
  return useSyncExternalStore(store.subscribe, () => selector(store.get()), () => selector(store.get()));
}

export interface AppProviderProps {
  store: AppStore;
  children: ReactNode;
  /** desligado nos testes que controlam a conexão à mão */
  monitorConnectivity?: boolean;
}

export function AppProvider({ store, children, monitorConnectivity = true }: AppProviderProps) {
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void store.init();
  }, [store]);

  // Voltar o foco para a janela recarrega a lista (P0.9 / AC-140).
  useEffect(() => {
    const onFocus = () => void store.reload();
    const onVisible = () => {
      if (document.visibilityState === 'visible') void store.reload();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [store]);

  useEffect(() => {
    if (!monitorConnectivity) return;
    return startConnectivityMonitor({ ping: () => store.backend.ping(), onChange: store.setOnline });
  }, [store, monitorConnectivity]);

  return <StoreContext.Provider value={store}>{children}</StoreContext.Provider>;
}
